import { useState } from 'react';

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { MonthCreditBreakdown } from './month-credit-breakdown';
import { Popover, PopoverAnchor, PopoverContent } from '../../components/shadcn/popover';
import { Skeleton } from '../../components/skeleton';

const fullFormatter = new Intl.NumberFormat('en-US');
const compact = (v: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);

function formatMonth(month: string): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

/**
 * Bar chart of credits used per calendar month. Clicking a month opens a popover showing
 * that month's credit usage broken down by matter.
 */
export function CreditMonthlyChart({
  monthlyUsage,
  organisationId,
  isFetching = false,
}: {
  monthlyUsage: Array<{ month: string; credits: number }>;
  organisationId: string;
  // True while a new range is loading — drives the loading skeleton / overlay.
  isFetching?: boolean;
}) {
  // The clicked month (YYYY-MM) plus the chart coordinates to anchor the popover to.
  const [selected, setSelected] = useState<{ month: string; x: number; y: number } | null>(null);

  const data = (monthlyUsage ?? []).map((m) => ({
    monthKey: m.month,
    month: formatMonth(m.month),
    credits: m.credits,
  }));

  // First load — no data to keep on screen yet.
  if (data.length === 0) {
    if (!isFetching) return null;
    return (
      <div className="p-4">
        <p className="font-medium text-gray-800">Credits used per month</p>
        <Skeleton className="mt-4 h-[280px] w-full rounded-md bg-gray-100" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <p className="font-medium text-gray-800">Credits used per month</p>
      <p className="mt-0.5 mb-4 text-xs text-gray-500">Click a month to see the breakdown by matter.</p>

      <Popover
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <div className="relative cursor-pointer">
          {selected && (
            <PopoverAnchor asChild>
              <span className="pointer-events-none absolute" style={{ left: selected.x, top: selected.y }} />
            </PopoverAnchor>
          )}
          <ResponsiveContainer
            width="100%"
            height={280}
            className={isFetching ? 'pointer-events-none animate-pulse grayscale' : ''}
          >
            <BarChart
              data={data}
              onClick={(state) => {
                const index = state.activeTooltipIndex;
                const coord = state.activeCoordinate;
                if (typeof index !== 'number' || !coord) return;
                const datum = data[index];
                if (!datum) return;
                setSelected({ month: datum.monthKey, x: coord.x ?? 0, y: coord.y ?? 0 });
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ transform: 'translate(0, 6)' }}
                className="fill-gray-500 text-xs"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                className="fill-gray-500 text-xs"
                tick={{ transform: 'translate(-4, 0)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={compact}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-gray-100)' }}
                content={({ payload, label }) => {
                  if (!payload || payload.length === 0) return null;
                  return (
                    <div className="rounded-lg border bg-white p-3 text-sm shadow-sm">
                      <p className="mb-2 border-b border-gray-200 pb-2 font-medium text-gray-700">{label}</p>
                      <div className="flex items-center justify-between gap-6">
                        <div className="flex items-center gap-1.5">
                          <span className="h-[3px] w-3.5 shrink-0 rounded-full bg-teal-500" />
                          <span className="text-gray-600">Credits</span>
                        </div>
                        <span className="font-medium text-gray-800">
                          {fullFormatter.format(payload[0].value as number)}
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="credits"
                fill="var(--color-teal-500)"
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
                name="Credits"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <PopoverContent align="center" side="top" className="w-72">
          {selected && <MonthCreditBreakdown organisationId={organisationId} month={selected.month} />}
        </PopoverContent>
      </Popover>
    </div>
  );
}
